/**
 * Resolve detected prosody issues to the existing knowledge entries
 * in `prosodyRef` / `phonicsRef` i18n bundles. Output is the bundle
 * we'll send to Gemini — only categories that *both* fired AND have
 * a matching ref get included, so the prompt stays tight and Gemini
 * can't ungrounded-suggest things we didn't detect.
 *
 * For acoustic categories that don't have a 1:1 ref entry (e.g.
 * `fast_speech`), we surface the raw metric with a Chinese label so
 * Gemini can still write a guidance line, just without "official"
 * back-reference text.
 */
import type { TFunction } from 'i18next';
import type { DetectedIssue, ProsodyIssueCategory } from './prosodyIssueDetector';

export interface RefEntry {
  why: string;
  tip: string;
  examples: string;
}

export interface ResolvedIssue {
  category: ProsodyIssueCategory;
  severity: 'low' | 'medium' | 'high';
  /** Short Chinese title for prompt readability — Gemini's seed for
   *  "what to call this issue" when speaking to the learner. */
  title: string;
  /** Linked knowledge entry from prosodyRef / phonicsRef (if any). */
  refKey?: string;
  refEntry?: RefEntry;
  /** Free-form one-liner with the detected numbers + offending word
   *  so Gemini can ground the suggestion in concrete evidence. */
  evidenceLine: string;
}

/**
 * Mapping from detected category → prosodyRef.cats[K]. Every acoustic
 * detector category now has a corresponding reference card on
 * /knowledge/prosody so the coach can always deep-link to "看詳細解說".
 *
 * Exceptions handled separately:
 *   - mispronunciation → /knowledge/phonics (different page;
 *     resolved in CoachingPanel via resolveCoachLink, not here)
 *   - omission → 'elision' (existing connected-speech entry that
 *     covers when sounds *should* drop vs accidental drop)
 */
export const PROSODY_REF_MAP: Partial<Record<ProsodyIssueCategory, string>> = {
  monotone: 'stress_timed',
  rising_falling_mismatch: 'rising_falling',
  // sentence_end_fade is partly rising_falling (statements should
  // descend, not fade), but the acoustic problem is more about
  // volume sustain than pitch direction. Reuse rising_falling — the
  // entry covers both.
  sentence_end_fade: 'rising_falling',
  fast_speech: 'pace_fast',
  slow_speech: 'pace_slow',
  hesitant: 'hesitant_pauses',
  low_voicing: 'voice_projection',
  omission: 'elision',
  // mispronunciation links to /knowledge/phonics, not this page —
  // see resolveCoachLink below.
};

/**
 * Resolve a tip's category to its full deep-link URL. Mispronunciation
 * routes to the phonics index; everything else with a prosodyRef
 * mapping routes to the prosody page anchored to its card. Categories
 * without any reference get null and the UI hides the link.
 */
export const resolveCoachLink = (category: string): string | null => {
  if (category === 'mispronunciation') return '/knowledge/phonics';
  const refKey = PROSODY_REF_MAP[category as ProsodyIssueCategory];
  if (refKey) return `/knowledge/prosody#${refKey}`;
  return null;
};

const TITLE_BY_CATEGORY: Record<ProsodyIssueCategory, string> = {
  monotone: '音調太平，整句聽起來偏單調',
  sentence_end_fade: '句尾收音不穩，聲音逐漸消失',
  fast_speech: '語速偏快',
  slow_speech: '語速偏慢，斷斷續續',
  hesitant: '停頓次數較多，節奏不連貫',
  low_voicing: '濁音不清晰，整體偏氣音',
  rising_falling_mismatch: '句尾語調方向不對',
  mispronunciation: '某個單字唸得跟原本不太一樣',
  omission: '漏唸了一個字',
};

const formatEvidence = (issue: DetectedIssue): string => {
  const e = issue.evidence;
  switch (issue.category) {
    case 'monotone':
      return `整段音高跨度只有 ${(e.value ?? 0).toFixed(1)} 個半音（自然朗讀通常 8 個半音以上）`;
    case 'sentence_end_fade':
      return `最後一個字「${e.word}」只有 ${Math.round((e.value ?? 0) * 100)}% frame 是有清楚音高的，正常會在 60% 以上`;
    case 'fast_speech':
      return `每秒 ${(e.value ?? 0).toFixed(1)} 個字（自然朗讀約 2.5-3.5 個字/秒）`;
    case 'slow_speech':
      return `每秒只 ${(e.value ?? 0).toFixed(1)} 個字（自然朗讀約 2.5-3.5 個字/秒）`;
    case 'hesitant':
      return `有 ${e.value} 處字與字之間停頓超過 0.5 秒${e.word ? `（第一處出現在「${e.word}」之前）` : ''}`;
    case 'low_voicing':
      return `濁音 frame 比例 ${Math.round((e.value ?? 0) * 100)}%（自然朗讀約 65% 以上）`;
    case 'rising_falling_mismatch':
      if (e.metric === 'question_should_rise_but_fell')
        return `這是疑問句，但末尾「${e.word}」的音調比整段平均還低 ${Math.abs(e.value ?? 0).toFixed(1)} 個半音（疑問句末尾應該揚起）`;
      return `這是陳述句，但末尾「${e.word}」的音調比整段平均高 ${(e.value ?? 0).toFixed(1)} 個半音（陳述句末尾應該下降）`;
    case 'mispronunciation':
      return `原文是「${e.word}」，但 ASR 聽到的像是「${e.heardAs}」 — 多家辨識結果一致時，幾乎可以確定發音偏掉了`;
    case 'omission':
      return `原文裡的「${e.word}」沒被任何 ASR 聽到，可能整個漏唸或唸得太輕`;
  }
};

export const resolveIssues = (
  issues: DetectedIssue[],
  t: TFunction,
): ResolvedIssue[] => {
  const out: ResolvedIssue[] = [];
  for (const issue of issues) {
    const refKey = PROSODY_REF_MAP[issue.category];
    let refEntry: RefEntry | undefined;
    if (refKey) {
      // i18next returnObjects to pull the structured entry. The
      // prosodyRef namespace ships zh-TW + 8 others; we read the
      // current locale's bundle.
      const raw = t(`cats.${refKey}`, {
        ns: 'prosodyRef',
        returnObjects: true,
      }) as unknown;
      if (raw && typeof raw === 'object' && 'why' in raw && 'tip' in raw) {
        refEntry = raw as RefEntry;
      }
    }
    out.push({
      category: issue.category,
      severity: issue.severity,
      title: TITLE_BY_CATEGORY[issue.category],
      refKey,
      refEntry,
      evidenceLine: formatEvidence(issue),
    });
  }
  return out;
};
